import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BadgeCheck } from "lucide-react";
import { api } from "../lib/api";
import { Reveal } from "../lib/ui";

const TONE = {
  Government: "border-white/25 text-white",
  "Public body": "border-ochre/50 text-ochre",
  Football: "border-maasai/60 text-maasai",
  "Related entity": "border-zinc-700 text-zinc-400",
};

const slug = (text) => text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

/**
 * Organisations we have worked with and can verify. Deliberately not called partners: no partnership,
 * sponsorship or endorsement is claimed in either direction.
 */
export const RecommendedEntities = ({ className = "" }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/entity").then((r) => setData(r.data.recommended)).catch(() => setData(null));
  }, []);

  if (!data) return null;

  return (
    <section className={`border-t border-zinc-900 bg-[#0b0b0b] py-16 md:py-20 ${className}`} data-testid="recommended-entities">
      <div className="mx-auto max-w-[1200px] px-5 md:px-10">
        <Reveal>
          <p className="overline text-ochre">Worked with and verified</p>
          <h2 className="display-2 mt-4 max-w-4xl">{data.heading}</h2>
          <p className="body-copy mt-6 max-w-3xl">{data.intro}</p>
        </Reveal>

        <div className="mt-12 grid gap-px bg-zinc-800 sm:grid-cols-2">
          {data.entities.map((e, i) => (
            <Reveal key={e.name} delay={Math.min(i * 0.04, 0.24)}>
              <div className="flex h-full flex-col bg-black p-6 md:p-7" data-testid={`recommended-${slug(e.name)}`}>
                <span className={`mono w-fit border px-2 py-1 text-[0.5rem] uppercase tracking-[0.16em] ${TONE[e.category] || TONE["Related entity"]}`}>
                  {e.category}
                </span>
                <h3 className="mt-4 font-display text-lg uppercase leading-tight tracking-tight text-white md:text-xl">
                  {e.name}
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-zinc-400">{e.note}</p>
                <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3">
                  <a
                    href={e.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`recommended-link-${slug(e.name)}`}
                    className="mono inline-flex items-center gap-1.5 text-[0.55rem] uppercase tracking-[0.16em] text-ochre transition-colors hover:text-white"
                  >
                    {e.link_label || "Their website"} <ArrowUpRight size={11} />
                  </a>
                  {e.internal ? (
                    <Link
                      to={e.internal}
                      data-testid={`recommended-internal-${slug(e.name)}`}
                      className="mono text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500 underline decoration-zinc-700 underline-offset-4 transition-colors hover:text-ochre"
                    >
                      How we relate
                    </Link>
                  ) : null}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <p className="mono mt-8 flex max-w-3xl items-start gap-3 text-[0.55rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-500" data-testid="recommended-note">
          <BadgeCheck size={15} className="mt-0.5 shrink-0 text-ochre" />
          {data.note}
        </p>
      </div>
    </section>
  );
};
